<template>
  <div v-if="!isLoading" class="p-4 max-w-xl mx-auto">
    <div
      @click="$router.back()"
      class="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 cursor-pointer shadow-md transition"
    >
      <BackSvg />
    </div>
    <div class="flex flex-col justify-center items-center space-y-1">
      <h2 class="text-lg text-zinc-700 text-center font-semibold">
        Reference Number
      </h2>
      <h4 class="text-sm text-zinc-500">#{{ transactionId }}</h4>
      <span
        class="text-sm px-3.5 py-2 rounded-full font-medium"
        :class="statusClass(itemData.status)"
      >
        {{ modifiedStatus(itemData.status) }}
      </span>
    </div>
    <div
      class="relative bg-white border border-gray-200 rounded-2xl shadow-lg my-4 overflow-hidden"
    >
      <!-- Left cutout -->
      <div
        class="absolute top-1/2 -left-3 -translate-y-1/2 w-6 h-6 bg-gray-50 border border-gray-200 rounded-full"
      ></div>

      <!-- Right cutout -->
      <div
        class="absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 bg-gray-50 border border-gray-200 rounded-full"
      ></div>

      <!-- Top Section -->
      <div
        :class="[
          'flex flex-col gap-3 justify-center items-center py-8',
          itemData.status === 'denied' || itemData.status === 'cancel'
            ? 'bg-gradient-to-r from-red-50 blind'
            : 'bg-gradient-to-r from-emerald-50 to-white',
        ]"
      >
        <span class="px-14">
          <img
            src="../../assets/images/consultation.gif"
            alt=""
            class="h-24 w-24"
          />
        </span>
      </div>

      <!-- Divider -->
      <div class="border-t border-dashed border-gray-300"></div>

      <!-- Bottom Section -->
      <div
        class="flex gap-2 justify-center flex-col items-center py-4 bg-gray-50"
      >
        <span class="text-xs text-zinc-400">
          <h5>{{ dateFormat(itemData.createdat) }}</h5>
        </span>
      </div>
    </div>
    <div>
      <h3 class="text-base text-zinc-700 font-medium">Transaction Details</h3>
    </div>

    <div
      class="mt-4 flex flex-col gap-3 rounded-md p-5 border-neutral-200 shadow-[0_10px_50px_rgba(0,0,0,0.1)] overflow-hidden bg-white"
    >
      <div class="flex gap-3 items-center">
        <img
          class="w-16 h-16 object-cover rounded-full shadow"
          :src="urlAvatar(itemData.profile)"
          alt=""
        />
        <span>
          <h3 class="font-medium text-base">{{ itemData.doctor }}</h3>
          <h4 class="text-sm text-zinc-400">
            {{ itemData.specialization }}
          </h4>
        </span>
      </div>
    </div>
    <div class="my-2 flex gap-2">
      <div
        class="flex-1 items-center rounded-md p-3 border-neutral-200 shadow-[0_10px_50px_rgba(0,0,0,0.1)] overflow-hidden bg-white"
      >
        <div class="flex flex-col gap-1">
          <h3 class="text-sm text-zinc-500 font-medium">Duration</h3>
          <div class="flex gap-2 items-center">
            <TimeSvg />

            <h1 class="text-base text-zinc-800">
              {{
                getDurationTime(
                  itemData.startTimeSession,
                  itemData.endTimeSession,
                )
              }}
            </h1>
          </div>
        </div>
      </div>
      <div
        class="flex-1 items-center rounded-md p-3 border-neutral-200 shadow-[0_10px_50px_rgba(0,0,0,0.1)] overflow-hidden bg-white"
      >
        <div class="flex flex-col gap-1">
          <h3 class="text-sm text-zinc-500 font-medium"></h3>
          <div class="flex gap-2 items-center">
            <CashSvg />
            <h1 class="text-base text-zinc-800"></h1>
          </div>
        </div>
      </div>
    </div>

    <!-- Cancel Button (Long Press with Progress) -->
    <div
      class="mb-3 w-full gap-9 flex items-center justify-center rounded-md p-5 border-neutral-200 shadow-[0_10px_50px_rgba(0,0,0,0.1)] overflow-hidden bg-white"
      v-if="itemData.status === 'completed'"
    >
      <div class="space-y-1" v-if="itemCheckIfhaveFiles.data.prescription">
        <span
          @click="downloadHandler()"
          class="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center"
        >
          <MedicineSvg />
        </span>
        <h4 class="text-xs">Prescription</h4>
      </div>

      <div class="space-y-1" v-if="itemCheckIfhaveFiles.data.labtest">
        <span
          @click="downloadTestProcedureHandler()"
          class="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center"
        >
          <LabSvg />
        </span>
        <h4 class="text-xs">Laboratory</h4>
      </div>

      <div class="space-y-1" v-if="itemCheckIfhaveFiles.data.medcert">
        <span
          @click="downloadMedicalCertificationHandler()"
          class="w-14 h-14 rounded-full bg-yellow-50 flex items-center justify-center"
        >
          <CertificateSvg />
        </span>
        <h4 class="text-xs">Certificate</h4>
      </div>
    </div>
    <div
      class="pb-6 text-zinc-400 flex flex-col gap-2 justify-center items-center"
    >
      <span class="text-sm">Customer Services Hotlines</span>
      <span class="text-xs text-center break-words w-full">
        {{ customerHotline }}
      </span>
    </div>
  </div>
  <div v-else>
    <div class="flex items-center justify-center h-48" v-if="isLoading">
      <svg
        class="w-10 h-10 animate-spin text-orange-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        ></path>
      </svg>
    </div>
  </div>
</template>

<script>
import BackSvg from "@/components/svgs/BackSvg.vue";
import { computed, inject, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import TimeSvg from "@/components/svgs/TimeSvg.vue";
import CashSvg from "@/components/svgs/CashSvg.vue";
import MedicineSvg from "@/components/svgs/MedicineSvg.vue";
import LabSvg from "@/components/svgs/LabSvg.vue";
import CertificateSvg from "@/components/svgs/CertificateSvg.vue";
import pdfGenerateHandler from "@/utils/prescription";
import pdfReferralGenerateHandler from "@/utils/testreferral";
import pdfMedCertGenerateHandler from "@/utils/medcertPDF";
export default {
  components: {
    TimeSvg,
    CashSvg,
    BackSvg,
    MedicineSvg,
    LabSvg,
    CertificateSvg,
  },
  setup() {
    const axios = inject("$axios");
    const router = inject("$router");
    const moment = inject("$moment");
    const route = useRoute();
    const transactionId = ref();
    const itemData = ref([]);
    const isLoading = ref(false);
    const itemCheckIfhaveFiles = ref([]);
    const { exportPDF } = pdfGenerateHandler();
    const { exportReferralPDF } = pdfReferralGenerateHandler();
    const { exportMedCertPDF } = pdfMedCertGenerateHandler();

    onMounted(() => {
      transactionId.value = route.params.id;
      currentQueuingHandler();
    });

    const currentQueuingHandler = async () => {
      try {
        isLoading.value = true;
        const { data } = await axios.get(
          `/api/patient/transaction/detail/${transactionId.value}`,
        );
        itemData.value = data.data;

        await checkIfHaveFileHandler();
        if (data.data === undefined) {
          router.push({ name: "NotFound" });
        }

        setTimeout(() => {
          isLoading.value = false;
        }, 800);
      } catch (error) {
        console.log(error);
      }
    };

    const queuingMessage = computed(() => {
      let status = itemData.value.status;
      let result;

      if (status === "in-line") {
        result = itemData.value.spot_number;
      } else if (status === "admitted") {
        result = "Your turn";
      } else if (status === "ringing") {
        result = "The doctor has joined the video room.";
      } else if (status === "in-session") {
        result = "You are currently in session";
      } else if (status === "completed") {
        result = "The consultation was completed.";
      }
      return result;
    });

    const dateFormat = (val) => {
      return moment(val).format("LLL");
    };
    const urlAvatar = (profile) => {
      return `${process.env.VUE_APP_API_URL}/api/portal/patient/profile?filename=${profile}`;
    };
    const getDurationTime = (startTime, endTime) => {
      let durationTime;

      if (startTime) {
        const start = moment(startTime);
        const end = moment(endTime);

        // Format as mm:ss
        const duration = moment.duration(end.diff(start));
        const formatted = moment.utc(duration.asMilliseconds()).format("mm:ss");
        durationTime = `${formatted} minutes`;
      } else {
        durationTime = "-";
      }

      return durationTime;
    };
    const modifiedStatus = (status) => {
      let result;
      if (status === "completed") {
        result = "Completed";
      } else if (status === "cancel") {
        result = "Cancel";
      } else if (status === "in-line") {
        result = "Pending";
      } else {
        result = "Denied";
      }

      return result;
    };

    const statusClass = (status) => {
      if (status === "completed") return "bg-green-100 text-green-700";
      if (status === "cancel") return "bg-red-100 text-red-700";
      if (status === "in-line") return "bg-gray-100 text-gray-700";
      return "bg-orange-100 text-orange-700";
    };
    const downloadHandler = async () => {
      try {
        const { data } = await axios.get(
          `/api/doctor/consultation/history/prescription/${transactionId.value}`,
        );

        console.log("pass", itemData.value);
        exportPDF({
          patient: itemData.value,
          prescriptions: data.data || [],
        });
      } catch (error) {
        console.log(error);
      }
    };

    const downloadTestProcedureHandler = async () => {
      try {
        const { data } = await axios.get(
          `/api/doctor/consultation/history/test-procedure/${transactionId.value}`,
        );

        if (!data.data || !data.data.length) {
          alert("No laboratory procedure found.");
          return;
        }
        exportReferralPDF(data.data, itemData.value);
      } catch (error) {
        console.log(error);
      }
    };
    const downloadMedicalCertificationHandler = async () => {
      try {
        const { data } = await axios.get(
          `/api/doctor/consultation/history/medical-certification/${transactionId.value}`,
        );

        if (!data.data || !data.data.length) {
          alert("No medcert found.");
          return;
        }

        exportMedCertPDF(data.data, itemData.value);
      } catch (error) {
        console.log(error);
      }
    };
    const checkIfHaveFileHandler = async () => {
      try {
        const { data } = await axios.get(
          `/api/doctor/data-entry/efiles/${transactionId.value}`,
        );
        itemCheckIfhaveFiles.value = data;
      } catch (error) {
        console.log(error);
      }
    };
    const customerHotline = computed(() => {
      return process.env.VUE_APP_IWC_HOTLINE;
    });
    return {
      transactionId,
      currentQueuingHandler,
      itemData,
      queuingMessage,
      dateFormat,
      isLoading,
      urlAvatar,
      getDurationTime,
      modifiedStatus,
      statusClass,
      downloadHandler,
      downloadTestProcedureHandler,
      exportPDF,
      exportReferralPDF,
      downloadMedicalCertificationHandler,
      checkIfHaveFileHandler,
      itemCheckIfhaveFiles,
      customerHotline,
    };
  },
};
</script>
